import moment from "moment";
import { getSubmissionPersonKey } from "./submissionStorage";
import {
  getMeasureTypeMeta,
  MEASURE_DIRECTIONS,
  MEASURE_ROLES,
  normalizeMeasureSeriesKey,
  QUESTION_MODES,
} from "./questions/questionStorage";

const WEIGHT_KEYS = /weight|body\s*fat|fat\s*%|bmi|waist|hip|chest|kg|lbs/i;
const SCORE_KEYS =
  /energy|adherence|sessions|score|push.?up|grip|time.?trial|lean\s*mass|inbody/i;

/** Known library keys → hard series link when DB fields are missing. */
const LIBRARY_SERIES_HINTS = {
  q_starting_weight: { seriesKey: "weight", role: MEASURE_ROLES.BASELINE },
  q_current_weight: { seriesKey: "weight", role: MEASURE_ROLES.PROGRESS },
  q_target_weight: {
    seriesKey: "weight_target",
    role: MEASURE_ROLES.TARGET,
  },
};

/** Pull first number from values like "82 kg", "28%", "7". */
export const parseMeasurableNumber = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const match = String(raw).replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  if (!match) return null;
  const n = Number(match[0]);
  return Number.isFinite(n) ? n : null;
};

export const parseMeasurableUnit = (raw) => {
  if (raw == null) return "";
  const text = String(raw);
  const match = text.match(
    /\d+(\.\d+)?\s*([a-zA-Z%]+(?:\s*[a-zA-Z]+)*)\s*$/
  );
  return match?.[2]?.trim() || "";
};

const normalizeLabel = (label = "") =>
  String(label)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

const titleCase = (text = "") =>
  String(text)
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

const inferRoleFromLabel = (label = "") => {
  const n = normalizeLabel(label);
  if (/target/.test(n) && /weight/.test(n)) return MEASURE_ROLES.TARGET;
  if (/^starting\b/.test(n) || /\bbaseline\b/.test(n)) {
    return MEASURE_ROLES.BASELINE;
  }
  if (/^current\b/.test(n) || /\bprogress\b/.test(n)) {
    return MEASURE_ROLES.PROGRESS;
  }
  return MEASURE_ROLES.NONE;
};

const resolveSeriesMeta = (q) => {
  const libraryKey = String(q.libraryKey || q.libraryQuestionId || "").trim();
  const hint = LIBRARY_SERIES_HINTS[libraryKey] || null;
  let seriesKey =
    normalizeMeasureSeriesKey(q.measureSeriesKey) ||
    hint?.seriesKey ||
    "";
  const role =
    q.measureRole || hint?.role || inferRoleFromLabel(q.label) || "";
  // Weight baseline/progress without an explicit key still share one series
  if (
    !seriesKey &&
    (q.measureType === "weight" || WEIGHT_KEYS.test(q.label || ""))
  ) {
    if (role === MEASURE_ROLES.TARGET) seriesKey = "weight_target";
    else if (
      role === MEASURE_ROLES.BASELINE ||
      role === MEASURE_ROLES.PROGRESS
    ) {
      seriesKey = "weight";
    }
  }
  return { seriesKey, role };
};

const collectTrackerMeasurableQuestions = (tracker) => {
  if (!tracker) return [];
  const out = [];
  const push = (q) => {
    if (!q) return;
    const measurable =
      q.measurable === true ||
      q.mode === QUESTION_MODES.MEASURABLE ||
      q.mode === "measurable";
    if (!measurable && !WEIGHT_KEYS.test(q.label || "")) return;
    const { seriesKey, role } = resolveSeriesMeta(q);
    out.push({
      id: q.id || q.questionId || normalizeLabel(q.label),
      label: q.label || "Measure",
      measureType: q.measureType || "",
      measureSeriesKey: seriesKey,
      measureRole: role,
      measureDirection:
        q.measureDirection ||
        getMeasureTypeMeta(q.measureType)?.defaultDirection ||
        MEASURE_DIRECTIONS.DESCENDING,
      unit: q.defaultUnit || (q.units || [])[0] || "",
    });
  };

  (tracker.personalInfoQuestions || []).forEach(push);
  (tracker.sharedQuestions || []).forEach(push);
  (tracker.questions || []).forEach(push);
  (tracker.forms || []).forEach((form) => {
    (form.questions || []).forEach(push);
  });
  return out;
};

const findAnswerValue = (answers = {}, label) => {
  if (!answers || typeof answers !== "object") return null;
  if (answers[label] != null) return answers[label];
  const want = normalizeLabel(label);
  const entry = Object.entries(answers).find(
    ([key]) =>
      normalizeLabel(key) === want ||
      normalizeLabel(key).includes(want) ||
      want.includes(normalizeLabel(key))
  );
  return entry ? entry[1] : null;
};

/**
 * Detect measurable-looking answer keys when tracker questions are sparse.
 */
const inferMetricLabelsFromAnswers = (submissions = []) => {
  const counts = new Map();
  submissions.forEach((row) => {
    Object.entries(row.answers || {}).forEach(([key, value]) => {
      const n = parseMeasurableNumber(value);
      if (n == null) return;
      if (!WEIGHT_KEYS.test(key) && !SCORE_KEYS.test(key)) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
  });
  return Array.from(counts.entries())
    .filter(([, count]) => count >= 1)
    .map(([label]) => {
      const role = inferRoleFromLabel(label);
      const isWeight = WEIGHT_KEYS.test(label);
      const seriesKey =
        role === MEASURE_ROLES.TARGET
          ? "weight_target"
          : isWeight &&
              (role === MEASURE_ROLES.BASELINE ||
                role === MEASURE_ROLES.PROGRESS)
            ? "weight"
            : "";
      return {
        id: normalizeLabel(label),
        label,
        measureType: isWeight ? "weight" : "",
        measureSeriesKey: seriesKey,
        measureRole: role,
        measureDirection: isWeight
          ? MEASURE_DIRECTIONS.ASCENDING
          : MEASURE_DIRECTIONS.DESCENDING,
        unit: "",
      };
    });
};

const directionIsLowerBetter = (direction) =>
  direction === MEASURE_DIRECTIONS.ASCENDING || direction === "ascending";

const seriesDisplayLabel = (seriesKey, members) => {
  if (seriesKey === "weight") return "Weight";
  if (seriesKey === "weight_target") return "Target Weight";
  const progress = members.find((m) => m.measureRole === MEASURE_ROLES.PROGRESS);
  if (progress?.label) {
    return progress.label
      .replace(/^Starting\s+/i, "")
      .replace(/^Current\s+/i, "")
      .trim();
  }
  const first = members[0];
  if (first?.label) {
    return first.label
      .replace(/^Starting\s+/i, "")
      .replace(/^Current\s+/i, "")
      .trim();
  }
  return titleCase(seriesKey) || "Measure";
};

/**
 * Group measurable defs into series. Hard link = shared measureSeriesKey
 * (Starting + Current Weight). Target stays its own series.
 */
const groupIntoSeries = (defs) => {
  const groups = new Map();
  defs.forEach((def, index) => {
    let key = def.measureSeriesKey;
    if (!key) {
      // Legacy fallback: strip starting/current prefix for weight-like labels
      let legacy = normalizeLabel(def.label).replace(
        /^(starting|current)\s+/,
        ""
      );
      if (/target/.test(legacy) && /weight/.test(legacy)) {
        legacy = "target weight";
      }
      key = normalizeMeasureSeriesKey(legacy) || `solo_${def.id || index}`;
    }
    if (def.measureRole === MEASURE_ROLES.TARGET && key === "weight") {
      key = "weight_target";
    }
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(def);
  });
  return groups;
};

const pickRawForRow = (row, members) => {
  const progress = members.filter(
    (m) => m.measureRole === MEASURE_ROLES.PROGRESS
  );
  const baseline = members.filter(
    (m) => m.measureRole === MEASURE_ROLES.BASELINE
  );
  const others = members.filter(
    (m) =>
      m.measureRole !== MEASURE_ROLES.PROGRESS &&
      m.measureRole !== MEASURE_ROLES.BASELINE
  );
  const order = [...progress, ...others, ...baseline];
  for (const def of order) {
    const raw = findAnswerValue(row.answers, def.label);
    if (raw != null && raw !== "") return { raw, def };
  }
  return { raw: null, def: members[0] };
};

const buildMetricFromPoints = (id, label, def, points, submission) => {
  if (!points.length) return null;
  const first = points[0];
  const last = points[points.length - 1];
  const currentPoint = points.find((p) => p.isCurrent) || last;
  const delta = currentPoint.value - first.value;
  const pct =
    first.value !== 0 ? (delta / Math.abs(first.value)) * 100 : null;
  const lowerBetter = directionIsLowerBetter(def.measureDirection);
  const improved =
    Math.abs(delta) < 0.0001
      ? "flat"
      : lowerBetter
        ? delta < 0
          ? "up"
          : "down"
        : delta > 0
          ? "up"
          : "down";

  return {
    id,
    label,
    measureType: def.measureType,
    measureDirection: def.measureDirection,
    measureSeriesKey: def.measureSeriesKey || "",
    unit: currentPoint.unit || first.unit || def.unit || "",
    points,
    baseline: first.value,
    current: currentPoint.value,
    delta,
    pctChange: pct,
    improved,
    lowerBetter,
  };
};

/**
 * Build measurable performance series for the person on this submission's tracker.
 * `historyRows` = API-loaded submissions for the tracker (falls back to just
 * this submission); `tracker` = API-loaded tracker definition (optional).
 */
export const buildSubmissionPerformance = (
  submission,
  historyRows = null,
  tracker = null
) => {
  if (!submission?.trackerId) {
    return { metrics: [], personKey: "", historyCount: 0 };
  }

  const personKey = getSubmissionPersonKey(submission);
  const pool = Array.isArray(historyRows) ? historyRows : [submission];
  const all = pool
    .filter((row) => getSubmissionPersonKey(row) === personKey)
    .sort(
      (a, b) =>
        new Date(a.submittedAt || a.createdAt || 0) -
        new Date(b.submittedAt || b.createdAt || 0)
    );

  let defs = collectTrackerMeasurableQuestions(tracker);
  if (!defs.length) {
    defs = inferMetricLabelsFromAnswers(all);
  }

  const seriesGroups = groupIntoSeries(defs);
  const metrics = [];

  seriesGroups.forEach((members, seriesKey) => {
    const primary =
      members.find((m) => m.measureRole === MEASURE_ROLES.PROGRESS) ||
      members.find((m) => m.measureRole === MEASURE_ROLES.BASELINE) ||
      members[0];
    const label = seriesDisplayLabel(seriesKey, members);
    const points = [];

    all.forEach((row) => {
      const { raw, def } = pickRawForRow(row, members);
      const value = parseMeasurableNumber(raw);
      if (value == null) return;
      const unit =
        parseMeasurableUnit(raw) ||
        def?.unit ||
        primary.unit ||
        getMeasureTypeMeta(primary.measureType)?.units?.[0] ||
        "";
      points.push({
        submissionId: row.id,
        formName: row.formName || "Submission",
        submittedAt: row.submittedAt || row.createdAt,
        value,
        unit,
        raw,
        isCurrent: row.id === submission.id,
        role: def?.measureRole || "",
      });
    });

    const metric = buildMetricFromPoints(
      seriesKey || primary.id,
      label,
      primary,
      points,
      submission
    );
    if (metric) metrics.push(metric);
  });

  const ranked = metrics.sort((a, b) => {
    const score = (m) =>
      (m.points.length >= 2 ? 100 : 0) +
      (m.improved !== "flat" ? 20 : 0) +
      (m.points.some((p) => p.isCurrent) ? 10 : 0) +
      m.points.length;
    return score(b) - score(a);
  });

  return {
    metrics: ranked,
    personKey,
    historyCount: all.length,
  };
};

export const formatMetricValue = (value, unit) => {
  if (value == null || Number.isNaN(value)) return "—";
  const rounded =
    Math.abs(value) >= 100 ? Math.round(value) : Math.round(value * 10) / 10;
  return unit ? `${rounded} ${unit}` : String(rounded);
};

export const formatDelta = (delta, unit, improved) => {
  if (delta == null || Number.isNaN(delta)) return "—";
  const sign = delta > 0 ? "+" : "";
  const rounded = Math.round(delta * 10) / 10;
  const text = unit ? `${sign}${rounded} ${unit}` : `${sign}${rounded}`;
  if (improved === "flat") return "No change";
  return text;
};

export const formatPointLabel = (point) => {
  if (point?.submittedAt) {
    return moment(point.submittedAt).format("DD MMM");
  }
  return point?.formName || "—";
};
